import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  DUPLICATE_JOB_ERROR,
  NO_CARD_ERROR,
  SHOW_CHILD_MISSING_TERMS_POPUP,
  SHOW_JOBS_INVITE_CONFIRMATION_POPUP,
  SHOW_MESSAGE,
  isOverlapingJob,
  showJobCreatePaymentError,
  showMessage,
} from '~/actions';
import { type IShiftDetailsFormValues } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { usePaymentEligibility } from '~/hooks/usePaymentEligibility';
import { MultiDayShiftsService } from '../services';
import { type IPostShiftsResult } from '../types';

interface IPostShiftsMutationInput {
  formValues: IShiftDetailsFormValues;
  duplicate?: boolean;
}

interface IUsePostShiftsParams {
  officeId: string;
  allowNavigation: () => void;
}

export const usePostShifts = ({ officeId, allowNavigation }: IUsePostShiftsParams) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasValidPaymentMethod = usePaymentEligibility(officeId).isEligible;

  return useMutation({
    mutationFn: ({ formValues, duplicate }: IPostShiftsMutationInput) =>
      MultiDayShiftsService.postShifts(formValues, officeId, duplicate),

    onSuccess: (result: IPostShiftsResult, { formValues }: IPostShiftsMutationInput) => {
      allowNavigation();
      navigate('/');

      // A new shift changes the dashboard counts and the open-shift list. Both
      // dashboard queries are cached, so invalidate them here to keep the tab
      // count and the shift cards in sync (matches cancel/confirm/decline).
      queryClient.invalidateQueries({ queryKey: ['dashboard-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-jobs-info'] });

      if (result.hadInvites) {
        dispatch({
          type: SHOW_MESSAGE,
          payload: {
            title: 'Shifts Posted & Invites Successfully Sent!',
            message: React.createElement(
              'span',
              null,
              'Your shifts were sent to the GoTu community, and invited professionals received alerts for days they\u2019re available.',
              React.createElement('br'),
              'Note: Professionals won\u2019t receive invites for days in which they\u2019re unavailable.',
            ),
            isSuccess: true,
            buttonText: 'Close',
          },
        });
      } else if (formValues.autoConfirm.favorites || !hasValidPaymentMethod) {
        // Auto-confirm favorites, or an office that can't pay: in the latter case
        // inviting professionals is unavailable, so skip the invite recommendation and
        // show the plain success confirmation instead.
        dispatch(
          showMessage({
            title: 'Shift Posted Successfully!',
            message:
              'We\u2019re reaching out to all professionals in your area. Your shifts were sent successfully to the GoTu community. You will be notified when your offers have been accepted.',
            isSuccess: true,
          }),
        );
      } else {
        dispatch({
          type: SHOW_JOBS_INVITE_CONFIRMATION_POPUP,
          payload: {
            jobIds: result.response.jobs,
            profession: result.response.profession,
            officeId,
          },
        });
      }
    },

    onError: (error: AxiosError<{ message?: string }>) => {
      const response = error?.response;
      const status = response?.status;
      const errorMessage: string = response?.data?.message || '';

      if (status === DUPLICATE_JOB_ERROR) {
        dispatch(isOverlapingJob(response?.data?.message));
      } else if (status === NO_CARD_ERROR) {
        dispatch(showJobCreatePaymentError());
      } else if (errorMessage.includes('Cannot post shifts')) {
        const popupMappings = [
          {
            substring: "GoTu's Policies",
            payload: { isTCMissing: true, isPSAMissing: true },
          },
          {
            substring: "GoTu's Terms and Conditions",
            payload: { isTCMissing: true, isPSAMissing: false },
          },
          {
            substring: "GoTu's Personnel Service Agreement",
            payload: { isTCMissing: false, isPSAMissing: true },
          },
        ];

        const matched = popupMappings.find(({ substring }) => errorMessage.includes(substring));

        if (matched) {
          dispatch({
            type: SHOW_CHILD_MISSING_TERMS_POPUP,
            payload: { ...matched.payload, officeId },
          });
        } else {
          dispatch(
            showMessage({
              title: 'Sorry!',
              message: errorMessage || 'An unexpected error occurred.',
              isError: true,
              buttonText: 'Close',
            }),
          );
        }
      } else {
        dispatch(
          showMessage({
            title: 'Sorry!',
            message: errorMessage || 'An unexpected error occurred.',
            isError: true,
            buttonText: 'Close',
          }),
        );
      }
    },
  });
};
